import { Link } from '@inertiajs/react'
import { SortableHeaderButton } from '@/components/design-system/table/SortableHeaderButton'
import TelemetryPagination from './TelemetryPagination'
import { formatDate, formatDuration, highlightMatch } from '../utils'
import TokensWithBreakdown from './TokensWithBreakdown'
import type { Pagination, TokenBreakdown } from '../types'

interface Session {
  id: string
  sessionId: string
  label: string | null
  lastChannel: string | null
  model: string | null
  totalEvents: number
  totalTokens: number
  tokenBreakdown: TokenBreakdown | null
  startedAt: string | null
  lastEventAt: string | null
  matchSnippet: string | null
  matchRole: string | null
}

interface SessionsTableProps {
  sessions: Session[]
  digitalWorkerId: string
  pagination: Pagination
  sortBy: string
  sortDirection: 'asc' | 'desc'
  searchQuery: string
  onSort: (sortKey: string) => void
  onPageChange: (page: number) => void
}

export default function SessionsTable({ sessions, digitalWorkerId, pagination, sortBy, sortDirection, searchQuery, onSort, onPageChange }: SessionsTableProps) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-auto max-h-[calc(100vh-680px)]">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Sessão</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Canal</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Modelo</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Duração</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Eventos</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortableHeaderButton label="Tokens" sortKey="total_tokens" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortableHeaderButton label="Última Atividade" sortKey="last_event_at" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sessions.map((session) => (
              <tr key={session.id} className="hover:bg-accent transition-colors">
                <td className="px-4 py-3">
                  <Link href={`/cc/telemetry/digital_workers/${digitalWorkerId}/sessions/${session.sessionId}`} className="text-sm font-medium text-foreground hover:text-blue-600">
                    {session.label || (<span className="font-mono text-xs">{session.sessionId.slice(0, 8)}</span>)}
                  </Link>
                  {session.matchSnippet && (
                    <p className="mt-1 text-xs text-muted-foreground truncate max-w-md">
                      <span className="text-muted-foreground font-medium">{session.matchRole === 'user' ? 'Usuário' : 'Assistente'}:</span>{' '}
                      {highlightMatch(session.matchSnippet, searchQuery)}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  {session.lastChannel ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent text-foreground">{session.lastChannel}</span>
                  ) : (<span className="text-sm text-muted-foreground">—</span>)}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{session.model || '—'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{formatDuration(session.startedAt, session.lastEventAt)}</td>
                <td className="px-4 py-3 text-sm text-foreground">{session.totalEvents}</td>
                <td className="px-4 py-3 text-sm text-foreground"><TokensWithBreakdown total={session.totalTokens} breakdown={session.tokenBreakdown} /></td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{session.lastEventAt ? formatDate(session.lastEventAt) : '—'}</td>
              </tr>
            ))}
            {sessions.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma sessão encontrada.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TelemetryPagination pagination={pagination} onPageChange={onPageChange} />
    </div>
  )
}
